import React from "react";
import { Radio, Calendar, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { toEmbedUrl } from "@/lib/video";
import VideoPlayer from "@/components/site/VideoPlayer";

function formatFrenchDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function LiveSection({ live }) {
  const isLive = live?.is_live;
  const embed = toEmbedUrl(live?.platform, live?.stream_url);

  return (
    <section id="direct" className="relative py-24 lg:py-32 bg-deepblue-900 text-white overflow-hidden">
      <div className="absolute inset-0 grain opacity-70" />
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-gold/5 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.3em] uppercase mb-4">
            <Radio className="w-3.5 h-3.5" /> Diffusion en direct
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight">
            {isLive ? "Nous sommes en direct" : "Rejoignez-nous dimanche"}
          </h2>
          <p className="mt-4 text-white/70 text-lg">
            Vivez le culte de chez vous — que vous soyez malade, en voyage ou en famille.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-2xl">
              {isLive && (
                <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-bold tracking-wider">
                  <span className="live-dot w-2 h-2 rounded-full bg-white" />
                  EN DIRECT
                </div>
              )}
              <div className="video-16-9 bg-black" data-testid="live-video-container">
                {isLive && embed ? (
                  <VideoPlayer type={live.platform} url={embed} autoPlay muted title="Diffusion en direct" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-deepblue-800 to-deepblue-900 text-center px-6">
                    <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mb-6">
                      <Radio className="w-9 h-9 text-gold" />
                    </div>
                    <div className="font-serif text-2xl sm:text-3xl">Aucune diffusion en cours</div>
                    <div className="text-white/60 mt-2 max-w-md">
                      Le direct sera activé quelques minutes avant le prochain culte.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 space-y-4">
            <div className="rounded-2xl p-6 bg-white/5 border border-white/10 backdrop-blur-sm" data-testid="next-service-card">
              <div className="flex items-center gap-2 text-gold text-xs tracking-[0.25em] uppercase mb-3">
                <Calendar className="w-3.5 h-3.5" /> Prochain culte
              </div>
              <div className="font-serif text-2xl leading-tight">
                {live?.next_service_title || "Culte du dimanche"}
              </div>
              <div className="mt-2 text-white/70 text-sm capitalize">
                {formatFrenchDate(live?.next_service_date) || "Dimanche 10h00"}
              </div>
              {live?.next_service_description && (
                <p className="mt-4 text-white/70 text-sm leading-relaxed">{live.next_service_description}</p>
              )}
              <Button
                data-testid="reminder-btn"
                onClick={() => toast.success("Rappel enregistré", { description: "Nous vous notifierons avant le début du culte." })}
                className="w-full mt-6 bg-gold hover:bg-gold-hover text-white rounded-full"
              >
                <Bell className="w-4 h-4 mr-2" /> Me rappeler
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
